<template>
  <div class="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-[#E5E7EB] dark:border-[#374151] p-6 mb-6">
    
    <!-- Filter Content -->
    <div class="flex flex-col lg:flex-row gap-4 items-center justify-between">
      
      <!-- Search Section -->
      <div class="flex-1 max-w-sm w-full">
        <div class="relative group">
          <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <i class="material-symbols-outlined text-[#9CA3AF] group-focus-within:text-[#6B7280] transition-colors duration-200 text-lg">search</i>
          </div>
          <input
            type="text"
            v-model="searchQuery"
            @input="searchQuery = $event.target.value"
            :placeholder="directT('ecommerce.productsGrid.searchHere')"
            class="w-full h-10 pr-10 pl-3 rounded-lg bg-[#F9FAFB] dark:bg-[#0f172a] border border-[#E5E7EB] dark:border-[#334155] text-[#1a1a1a] dark:text-white placeholder-[#9CA3AF] dark:placeholder-[#64748B] focus:outline-none focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/10 transition-all duration-200 text-sm"
          />
        </div>
      </div>
      
      <!-- Filter Controls -->
      <div class="flex flex-wrap items-center gap-2">
        
        <!-- Category Filter -->
        <div class="relative">
          <button
            @click="toggleCategoryFilter"
            class="inline-flex items-center px-3 py-2 rounded-lg bg-[#F9FAFB] dark:bg-[#0f172a] border border-[#E5E7EB] dark:border-[#334155] text-[#6B7280] dark:text-[#94A3B8] font-medium hover:bg-[#F3F4F6] dark:hover:bg-[#1e293b] hover:border-[#D1D5DB] dark:hover:border-[#475569] transition-all duration-200 text-sm"
          >
            <i class="material-symbols-outlined mr-1.5 text-base">category</i>
            {{ directT('ecommerce.productsGrid.category') }}
            <i class="material-symbols-outlined ml-1.5 transition-transform duration-200 text-base" :class="categoryFilterOpen ? 'rotate-180' : ''">expand_more</i>
          </button>
          
          <div
            v-show="categoryFilterOpen"
            class="absolute top-full mt-1 right-0 bg-white dark:bg-[#1e293b] rounded-lg shadow-lg border border-[#E5E7EB] dark:border-[#334155] min-w-[180px] z-10 overflow-hidden"
          >
            <div class="p-1">
              <button
                v-for="category in categories"
                :key="category"
                @click="selectCategory(category)"
                class="w-full text-right px-3 py-2 rounded-md text-[#374151] dark:text-[#E2E8F0] hover:bg-[#F3F4F6] dark:hover:bg-[#334155] transition-all duration-150 font-medium text-sm"
              >
                {{ directT(category) }}
              </button>
            </div>
          </div>
        </div>
        
        <!-- Price Range Filter -->
        <div class="relative">
          <button
            @click="togglePriceFilter"
            class="inline-flex items-center px-3 py-2 rounded-lg bg-[#F9FAFB] dark:bg-[#0f172a] border border-[#E5E7EB] dark:border-[#334155] text-[#6B7280] dark:text-[#94A3B8] font-medium hover:bg-[#F3F4F6] dark:hover:bg-[#1e293b] hover:border-[#D1D5DB] dark:hover:border-[#475569] transition-all duration-200 text-sm"
          >
            <i class="material-symbols-outlined mr-1.5 text-base">payments</i>
            {{ directT('ecommerce.productsGrid.price') }}
            <i class="material-symbols-outlined ml-1.5 transition-transform duration-200 text-base" :class="priceFilterOpen ? 'rotate-180' : ''">expand_more</i>
          </button>
          
          <div
            v-show="priceFilterOpen"
            class="absolute top-full mt-1 right-0 bg-white dark:bg-[#1e293b] rounded-lg shadow-lg border border-[#E5E7EB] dark:border-[#334155] w-64 z-10 overflow-hidden"
          >
            <div class="p-4">
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-xs font-medium text-[#6B7280] dark:text-[#94A3B8] mb-1">{{ directT('ecommerce.productsGrid.min') }}</label>
                  <input
                    type="number"
                    v-model="minPrice"
                    placeholder="0"
                    class="w-full h-8 rounded-md border border-[#E5E7EB] dark:border-[#334155] bg-[#F9FAFB] dark:bg-[#0f172a] px-2 text-[#374151] dark:text-[#E2E8F0] text-sm outline-none focus:border-[#6366F1] focus:ring-1 focus:ring-[#6366F1]"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-[#6B7280] dark:text-[#94A3B8] mb-1">{{ directT('ecommerce.productsGrid.max') }}</label>
                  <input
                    type="number"
                    v-model="maxPrice"
                    placeholder="∞"
                    class="w-full h-8 rounded-md border border-[#E5E7EB] dark:border-[#334155] bg-[#F9FAFB] dark:bg-[#0f172a] px-2 text-[#374151] dark:text-[#E2E8F0] text-sm outline-none focus:border-[#6366F1] focus:ring-1 focus:ring-[#6366F1]"
                  />
                </div>
              </div>
              <button
                @click="applyPriceFilter"
                class="w-full mt-3 px-3 py-2 bg-[#6366F1] text-white rounded-md font-medium hover:bg-[#5048E5] transition-all duration-200 text-sm"
              >
                {{ directT('ecommerce.productsGrid.apply') }}
              </button>
            </div>
          </div>
        </div>
        
        <!-- Sort Dropdown -->
        <div class="relative">
          <button
            @click="toggleActive"
            class="inline-flex items-center px-3 py-2 rounded-lg bg-[#F9FAFB] dark:bg-[#0f172a] border border-[#E5E7EB] dark:border-[#334155] text-[#6B7280] dark:text-[#94A3B8] font-medium hover:bg-[#F3F4F6] dark:hover:bg-[#1e293b] hover:border-[#D1D5DB] dark:hover:border-[#475569] transition-all duration-200 text-sm"
            :class="{ 'bg-[#F3F4F6] dark:bg-[#1e293b] border-[#D1D5DB] dark:border-[#475569]': isActive }"
          >
            <i class="material-symbols-outlined mr-1.5 text-base">sort</i>
            {{ selectedSort || directT('ecommerce.productsGrid.sort') }}
            <i class="material-symbols-outlined ml-1.5 transition-transform duration-200 text-base" :class="isActive ? 'rotate-180' : ''">expand_more</i>
          </button>

          <div
            v-show="isActive"
            class="absolute top-full mt-1 right-0 bg-white dark:bg-[#1e293b] rounded-lg shadow-lg border border-[#E5E7EB] dark:border-[#334155] min-w-[200px] z-10 overflow-hidden"
          >
            <div class="p-1">
              <button
                v-for="option in sortOptions"
                :key="option.value"
                @click="selectSort(option)"
                class="w-full text-right px-3 py-2 rounded-md text-[#374151] dark:text-[#E2E8F0] hover:bg-[#F3F4F6] dark:hover:bg-[#334155] transition-all duration-150 font-medium text-sm flex items-center justify-between"
                :class="{ 'bg-[#F3F4F6] dark:bg-[#334155] text-[#6366F1]': selectedSort === option.label }"
              >
                <span>{{ directT(option.label) }}</span>
                <i v-if="selectedSort === option.label" class="material-symbols-outlined text-[#6366F1] text-base">check</i>
              </button>
            </div>
          </div>
        </div>
        
        <!-- Clear Filters Button -->
        <button
          v-if="hasActiveFilters"
          @click="clearAllFiltersEnhanced"
          class="inline-flex items-center px-3 py-2 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] text-[#DC2626] font-medium hover:bg-[#FEE2E2] transition-all duration-200 text-sm"
        >
          <i class="material-symbols-outlined mr-1.5 text-base">clear_all</i>
                      {{ directT('ecommerce.productsGrid.clear') }}
        </button>
        
      </div>
    </div>
    
    <!-- Active Filters Display -->
    <div v-if="hasActiveFilters" class="mt-4 pt-4 border-t border-[#F3F4F6] dark:border-[#334155]">
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-xs font-medium text-[#6B7280] dark:text-[#94A3B8]">{{ directT('ecommerce.productsGrid.activeFilters') }}</span>
        
        <div v-if="selectedCategory" class="flex items-center gap-1 px-2 py-1 bg-[#EDE9FE] text-[#6366F1] rounded-md border border-[#C4B5FD]">
          <span class="text-xs font-medium">{{ directT(selectedCategory) }}</span>
          <button @click="clearCategory" class="hover:bg-[#DDD6FE] rounded-full p-0.5 transition-colors duration-150">
            <i class="material-symbols-outlined text-xs">close</i>
          </button>
        </div>
        
        <div v-if="minPrice || maxPrice" class="flex items-center gap-1 px-2 py-1 bg-[#D1FAE5] text-[#059669] rounded-md border border-[#A7F3D0]">
          <span class="text-xs font-medium">₪{{ minPrice || 0 }} - ₪{{ maxPrice || '∞' }}</span>
          <button @click="clearPriceRange" class="hover:bg-[#A7F3D0] rounded-full p-0.5 transition-colors duration-150">
            <i class="material-symbols-outlined text-xs">close</i>
          </button>
        </div>
        
        <div v-if="searchQuery" class="flex items-center gap-1 px-2 py-1 bg-[#E0E7FF] text-[#3730A3] rounded-md border border-[#C7D2FE]">
          <span class="text-xs font-medium">"{{ searchQuery }}"</span>
          <button @click="clearSearch" class="hover:bg-[#C7D2FE] rounded-full p-0.5 transition-colors duration-150">
            <i class="material-symbols-outlined text-xs">close</i>
          </button>
        </div>
      </div>
    </div>
    
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useClickOutside } from "@/utils/useClickOutside";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

// Sort option interface
interface SortOption {
  value: string;
  label: string;
}

// Local filter states
const searchQuery = ref('');
const selectedCategory = ref('');
const selectedSort = ref('');
const minPrice = ref<number | null>(null);
const maxPrice = ref<number | null>(null);

// Emit events to parent
const emit = defineEmits<{
  'filter-change': [filters: {
    search: string;
    category: string;
    sort: string;
    minPrice: number | null;
    maxPrice: number | null;
  }]
}>();

// Function to emit filter changes
const emitFilterChange = () => {
  console.log('Emitting filter change:', {
    search: searchQuery.value,
    category: selectedCategory.value,
    sort: selectedSort.value,
    minPrice: minPrice.value,
    maxPrice: maxPrice.value
  });
  
  emit('filter-change', {
    search: searchQuery.value,
    category: selectedCategory.value,
    sort: selectedSort.value,
    minPrice: minPrice.value,
    maxPrice: maxPrice.value
  });
};

// Computed active filters
const hasActiveFilters = computed(() => {
  return !!(searchQuery.value || selectedCategory.value || minPrice.value || maxPrice.value);
});

// Clear filter functions
const clearCategory = () => {
  selectedCategory.value = '';
  emitFilterChange();
};

const clearPriceRange = () => {
  minPrice.value = null;
  maxPrice.value = null;
  emitFilterChange();
};

const clearSearch = () => {
  searchQuery.value = '';
  emitFilterChange();
};

const clearAllFilters = () => {
  searchQuery.value = '';
  selectedCategory.value = '';
  selectedSort.value = '';
  minPrice.value = null;
  maxPrice.value = null;
  emitFilterChange();
};

// Update filter functions
const updateCategory = (value: string) => {
  selectedCategory.value = value === 'ecommerce.productsGrid.categories.allCategories' ? '' : value;
  emitFilterChange();
};

const updateSort = (value: string) => {
  selectedSort.value = value;
  emitFilterChange();
};

const updatePriceRange = (min: number | null, max: number | null) => {
  minPrice.value = min;
  maxPrice.value = max;
  emitFilterChange();
};

// Watch for search input changes
watch(searchQuery, () => {
  emitFilterChange();
}, { debounce: 300 });

// Filter dropdown states
const categoryFilterOpen = ref(false);
const priceFilterOpen = ref(false);

// Dropdown functionality
const { isActive, toggleActive } = useClickOutside("dropdownToggle");

// Sample data
const categories = ref([
  'ecommerce.productsGrid.categories.allCategories',
  'ecommerce.productsGrid.categories.electronics',
  'ecommerce.productsGrid.categories.clothingFashion',
  'ecommerce.productsGrid.categories.homeGarden',
  'ecommerce.productsGrid.categories.sportsRecreation',
  'ecommerce.productsGrid.categories.foodDrinks',
  'ecommerce.productsGrid.categories.beautyHealth',
  'ecommerce.productsGrid.categories.toysKids'
]);

const sortOptions = ref<SortOption[]>([
  { value: 'default', label: 'ecommerce.productsGrid.sortOptions.default' },
  { value: 'price_low_high', label: 'ecommerce.productsGrid.sortOptions.priceLowHigh' },
  { value: 'price_high_low', label: 'ecommerce.productsGrid.sortOptions.priceHighLow' },
  { value: 'name_asc', label: 'ecommerce.productsGrid.sortOptions.nameAsc' },
  { value: 'name_desc', label: 'ecommerce.productsGrid.sortOptions.nameDesc' },
  { value: 'newest', label: 'ecommerce.productsGrid.sortOptions.newest' },
  { value: 'popular', label: 'ecommerce.productsGrid.sortOptions.popular' },
  { value: 'top_sales', label: 'ecommerce.productsGrid.sortOptions.topSales' }
]);

// Filter actions
const toggleCategoryFilter = () => {
  categoryFilterOpen.value = !categoryFilterOpen.value;
  priceFilterOpen.value = false;
};

const togglePriceFilter = () => {
  priceFilterOpen.value = !priceFilterOpen.value;
  categoryFilterOpen.value = false;
};

const selectCategory = (category: string) => {
  updateCategory(category);
  categoryFilterOpen.value = false;
};

const selectSort = (option: SortOption) => {
  updateSort(option.label);
  toggleActive();
};

const applyPriceFilter = () => {
  updatePriceRange(minPrice.value, maxPrice.value);
  priceFilterOpen.value = false;
};

// Enhanced clear all filters
const clearAllFiltersEnhanced = () => {
  clearAllFilters();
  categoryFilterOpen.value = false;
  priceFilterOpen.value = false;
};

// i18n setup
const { locale } = useI18n();

// Translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

// Direct translation function that bypasses vue-i18n
const directT = (key: string): string => {
  const parts = key.split('.');
  let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
  
  for (const part of parts) {
    if (!result || !(part in result)) {
      console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
      // Fallback to English if translation not found
      let enResult: TranslationObject = translationFiles.en;
      for (const fallbackPart of parts) {
        if (!enResult || !(fallbackPart in enResult)) return key;
        enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
      }
      return typeof enResult === 'string' ? enResult : key;
    }
    result = result[part as keyof typeof result] as TranslationObject;
  }
  return typeof result === 'string' ? result : key;
};
</script>
